/* @flow */
import * as React from 'react';
import HTML5Backend from 'react-dnd-html5-backend';
import { DragDropContext } from 'react-dnd';
import omit from 'lodash/fp/omit';

import BugNubTrigger from 'internal/components/BugNubTrigger';
import CurrentUser from 'auth/CurrentUser';
import Electron from 'lib/Electron';
import EventTracking, { getEventScreenName } from 'lib/EventTracking';
import PropTypes from 'prop-types';
import PusherActions from 'pusher/PusherActions';
import Feature from 'components/Feature';
import ScrollContext from 'components/ScrollContext';
import ToastsContainerNode from 'components/ToastsContainerNode';
import userAgent from 'lib/currentUserAgent';
import WebNotificationClient from 'notifications/WebNotificationClient';
import { markNewTransition, markAppMounted } from 'lib/Timer';
import { refreshAppIfOld } from 'utils/refreshAssets';
import { registerInterceptor, unregisterInterceptor } from 'singletons/xhrInterceptors';
import FloatingBoxesNode from 'components/FloatingBoxesNode';
import ModalsContainer from 'components/ModalsContainer';

import './styles/App.scss';

import type { RouterState } from 'lib/ReactRouterTypes';

// data is already up to date on load so defer opening Pusher connection to
// free up CPU and network.
const PUSHER_AUTH_DELAY_MS = 9000;
const IGNORE_ROUTE_PARAMS = omit(['uuid', 'flow', 'key', 'id', 'user_uuid', 'user_id']);

export class App extends React.Component<$FlowFixMeProps> {
  static propTypes = {
    routes: PropTypes.array.isRequired, // eslint-disable-line
    location: PropTypes.object.isRequired, // eslint-disable-line
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  // XXX all of these are available on context.router now
  // TODO delete here and update all dependents
  static childContextTypes = {
    routes: PropTypes.array.isRequired,
    location: PropTypes.object.isRequired,
    params: PropTypes.object.isRequired,
  };

  static onEnter(nextState: RouterState) {
    // Refresh the app if it's more than a day old when the user is navigating.
    refreshAppIfOld();
    markNewTransition(nextState.location.pathname);
    EventTracking.view(getEventScreenName(null, nextState), IGNORE_ROUTE_PARAMS(nextState.params));
  }

  static onChange(prevState: RouterState, nextState: RouterState) {
    window.prevLocation = prevState.location;
    App.onEnter(nextState);
  }

  // XXX all of these are available on context.router now
  // TODO delete here and update all dependents
  getChildContext() {
    return {
      routes: this.props.routes,
      location: this.props.location,
      params: this.props.params,
    };
  }

  componentDidMount() {
    markAppMounted();
    registerInterceptor('userchange-interceptor', this.userChangeInterceptor);

    this.handleUserSignIn();
    CurrentUser.addSignInListener(this.handleUserSignIn);
    CurrentUser.addSignInNoRoleListener(this.handleUserSignIn);
    Electron.bindIfAvailable(this.context.router);
    WebNotificationClient.handleNotifications(this.context.router);
  }

  componentWillUnmount() {
    unregisterInterceptor('userchange-interceptor');
    // leave 503-interceptor, probably should move elsewhere but another PR

    CurrentUser.removeSignInListener(this.handleUserSignIn);
  }

  handleUserSignIn = () => {
    // Users who logged in via a token will not have the access_token cookie set and will not be able to auth with pusher
    if (CurrentUser.isSignedIn() && !CurrentUser.isLimitedUserAuth()) {
      const user = CurrentUser.getUser();
      if (user && user.getPusherChannel()) {
        setTimeout(
          () => PusherActions.subscribeToChannel(user.getPusherChannel()),
          PUSHER_AUTH_DELAY_MS
        );
      }
    }
  };

  isOutOfSync = (response: Object) => {
    // TODO FIXME IE cache headers, resulting in this showing too much.
    if (userAgent.ie()) {
      return false;
    }

    const status = response.headers['status'];
    if (status && status.toLowerCase() === '304 not modified') {
      return false;
    }

    if (!CurrentUser.isSignedIn() || !response.headers['x-userid']) {
      return false;
    }

    return response.headers['x-userid'] !== CurrentUser.getUserId().toString();
  };

  // @returns whether the response was intercepted
  userChangeInterceptor = (response: Object) => {
    const isOutOfSync = this.isOutOfSync(response);
    if (!this.context.router.isActive('/user_changed') && isOutOfSync) {
      this.context.router.push('/user_changed');
      return true;
    }

    return false;
  };

  render() {
    return (
      <ScrollContext className="App" tabIndex={0}>
        <ToastsContainerNode />
        <FloatingBoxesNode />
        <ModalsContainer />
        {this.props.children}
        <Feature name="remind_employee">
          <BugNubTrigger />
        </Feature>
      </ScrollContext>
    );
  }
}

export default DragDropContext(HTML5Backend)(App);
